import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProjectCombobox } from "@/components/projects/ProjectCombobox";
import ApiKeySuccessDialog from "./ApiKeySuccessDialog";
import { Spinner } from "@/components/ui/spinner";
import { useCreateApiKey } from "../hooks/useApiKeys";
import {
  useProjectsQuery,
  useProjectsQueryScope,
} from "@/features/projects/hooks/useProjectsQuery";
import { projectQueryKeys } from "@/features/projects/query-keys";
import type { ApiKeyWithValue } from "../service";

const preprocessBudgetValue = (value: any) => {
  if (value === "" || value == null) {
    return null;
  }

  if (typeof value === "number") {
    return value;
  }

  const str = String(value).trim();
  const parsed = Number(str);
  return Number.isNaN(parsed) ? value : parsed;
};

const budgetNumberSchemaRequired = z
  .number()
  .positive("apiKeys.validationBudgetPositive")
  .refine((n) => /^\d+(?:\.\d{1,2})?$/.test(String(n)), {
    message: "apiKeys.validationBudgetPrecision",
  });

const baseFields = {
  name: z
    .string()
    .trim()
    .min(1, "apiKeys.validationNameRequired")
    .refine((name) => Array.from(name).length <= 50, "apiKeys.validationNameLength"),
  projectId: z.string().min(1, "apiKeys.validationProjectRequired"),
  expiresIn: z.string(),
};

const schemaNoFreq = z.object({
  ...baseFields,
  budget_reset_frequency: z.literal("").optional(),
  // When no frequency is selected, max_budget_usd must be null/absent
  max_budget_usd: z.null().optional(),
});

const schemaWithFreq = z.object({
  ...baseFields,
  budget_reset_frequency: z.enum(["HOURLY", "DAILY", "WEEKLY", "MONTHLY"]),
  max_budget_usd: z.preprocess(
    preprocessBudgetValue,
    budgetNumberSchemaRequired,
  ),
});

const createApiKeySchema = z.union([schemaNoFreq, schemaWithFreq]);

type CreateApiKeyFormData = z.infer<typeof createApiKeySchema>;

interface CreateApiKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onKeyCreated?: (key: ApiKeyWithValue) => void;
  returnFocusRef?: React.RefObject<HTMLButtonElement | null>;
}

interface CurrentProjectSelectionInput {
  selectedProjectId: string;
  selectionScopeIdentity: string;
  activeScopeIdentity: string;
  scopeInitialized: boolean;
  principalId: string;
  projects: ReadonlyArray<{ id: string }> | undefined;
}

export const isCurrentProjectSelection = ({
  selectedProjectId,
  selectionScopeIdentity,
  activeScopeIdentity,
  scopeInitialized,
  principalId,
  projects,
}: CurrentProjectSelectionInput) =>
  selectionScopeIdentity === activeScopeIdentity &&
  scopeInitialized &&
  Boolean(principalId) &&
  projects !== undefined &&
  projects.some((project) => project.id === selectedProjectId);

const CreateApiKeyDialog: React.FC<CreateApiKeyDialogProps> = ({
  open,
  onOpenChange,
  onKeyCreated,
  returnFocusRef,
}) => {
  const { t, i18n } = useTranslation();
  const [createdResult, setCreatedResult] = useState<{ session: object; key: ApiKeyWithValue } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef<object | null>(null);
  const mounted = useRef(false);
  const generatedKeyHandoffRef = useRef(false);

  const {
    register,
    control,
    clearErrors,
    getValues,
    reset,
    setError,
    setValue,
    formState: { errors },
  } = useForm<CreateApiKeyFormData>({
    resolver: zodResolver(createApiKeySchema),
    defaultValues: {
      name: "",
      projectId: "",
      expiresIn: "never",
      max_budget_usd: null,
      budget_reset_frequency: "",
    },
  });

  const mutation = useCreateApiKey();
  const resetMutation = mutation.reset;
  const { scope: projectScope, initialized: projectScopeInitialized } =
    useProjectsQueryScope();
  const projectScopeIdentity = projectQueryKeys
    .listScope(projectScope)
    .join("\u0000");
  const session = useMemo(() => ({ open, projectScopeIdentity }), [open, projectScopeIdentity]);
  const activeSession = useRef(session);
  const [observedSession, setObservedSession] = useState(session);
  if (observedSession !== session) {
    setObservedSession(session);
    setSubmitting(false);
    setCreatedResult(null);
  }
  const createdKey = open && createdResult?.session === session ? createdResult.key : null;
  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useLayoutEffect(() => {
    activeSession.current = session;
    inFlight.current = null;
    generatedKeyHandoffRef.current = false;
    resetMutation();
  }, [session, resetMutation]);
  const previousProjectScopeIdentityRef = useRef(projectScopeIdentity);

  const projectsQuery = useProjectsQuery(
    { includeShared: true },
    { enabled: open },
  );
  const projects = useMemo(
    () =>
      [...(projectsQuery.data ?? [])].sort((left, right) =>
        left.name.localeCompare(right.name),
      ),
    [projectsQuery.data],
  );
  const isProjectsLoading =
    projectsQuery.isPending || projectsQuery.isFetching;
  const isCreatingKey = mutation.isPending || submitting;

  useEffect(() => {
    if (previousProjectScopeIdentityRef.current === projectScopeIdentity) {
      return;
    }

    previousProjectScopeIdentityRef.current = projectScopeIdentity;
    setValue("projectId", "", {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    });
    clearErrors("projectId");
  }, [clearErrors, projectScopeIdentity, setValue]);

  const resetDialogState = useCallback(() => {
    generatedKeyHandoffRef.current = false;
    setCreatedResult(null);
    inFlight.current = null;
    setSubmitting(false);
    reset({
      name: "",
      projectId: "",
      expiresIn: "never",
      max_budget_usd: null,
      budget_reset_frequency: "",
    });
    mutation.reset();
  }, [mutation, reset]);

  const handleFormClose = useCallback(() => {
    resetDialogState();
    onOpenChange(false);
  }, [onOpenChange, resetDialogState]);

  const handleCreatedKeyClose = useCallback(() => {
    resetDialogState();
    onOpenChange(false);
  }, [onOpenChange, resetDialogState]);

  const setServerErrors = useCallback(
    (error: any) => {
      const detail = error?.response?.data?.detail;
      if (!Array.isArray(detail)) {
        return;
      }

      detail.forEach((item: any) => {
        if (!item?.loc || !item?.msg) {
          return;
        }

        const fieldName = item.loc[item.loc.length - 1];
        const errorMessage = item.msg;

        switch (fieldName) {
          case "budget_reset_frequency":
            setError("budget_reset_frequency", {
              type: "server",
              message: errorMessage,
            });
            break;
          case "max_budget_usd":
            setError("max_budget_usd", {
              type: "server",
              message: errorMessage,
            });
            break;
          case "project_id":
            setError("projectId", {
              type: "server",
              message: errorMessage,
            });
            break;
          case "name":
            setError("name", {
              type: "server",
              message: errorMessage,
            });
            break;
          default:
            break;
        }
      });
    },
    [setError],
  );

  const createKey = useCallback(
    (data: CreateApiKeyFormData) => {
      if (inFlight.current || mutation.isPending) return;
      const operation = {};
      inFlight.current = operation;
      setSubmitting(true);
      const isCurrent = () => mounted.current && activeSession.current === session && inFlight.current === operation;
      const release = () => {
        if (inFlight.current === operation) {
          inFlight.current = null;
          setSubmitting(false);
        }
      };
      let expires_at = null;

      if (data.expiresIn !== "never") {
        const days = Number.parseInt(data.expiresIn, 10);
        if (!Number.isNaN(days)) {
          const expirationDate = new Date();
          expirationDate.setDate(expirationDate.getDate() + days);
          expires_at = expirationDate.toISOString();
        }
      }

      const payload: any = {
        name: data.name.trim(),
        project_id: data.projectId,
        scopes: ["llm"],
        expires_at,
      };

      if (data.max_budget_usd != null) {
        payload.max_budget_usd = data.max_budget_usd;
      }

      // Only include budget_reset_frequency when a value was selected
      if (data.budget_reset_frequency) {
        payload.budget_reset_frequency = data.budget_reset_frequency;
      }

      mutation.mutate(payload, {
        onSuccess: (responseData) => {
          if (!isCurrent()) return;
          generatedKeyHandoffRef.current = true;
          setCreatedResult({ session, key: responseData });
          release();
          onKeyCreated?.(responseData);
        },
        onError: (error: any) => {
          if (!isCurrent()) return;
          setServerErrors(error);
          release();
        },
      });
    },
    [mutation, onKeyCreated, session, setServerErrors],
  );

  const handleCreateKey = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (inFlight.current || mutation.isPending) return;

      const formValues = getValues();

      const selectedProjectIsCurrent = isCurrentProjectSelection({
        selectedProjectId: formValues.projectId,
        selectionScopeIdentity: previousProjectScopeIdentityRef.current,
        activeScopeIdentity: projectScopeIdentity,
        scopeInitialized: projectScopeInitialized === true,
        principalId: projectScope.principalId,
        projects: projectsQuery.data,
      });

      if (formValues.projectId && !selectedProjectIsCurrent) {
        setValue("projectId", "", {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
        setError("projectId", {
          type: "manual",
          message: t("apiKeys.validationProjectRequired"),
        });
        return;
      }

      // Normalize max budget input so empty string is treated as null and
      // numeric strings are parsed to numbers. This ensures consistent
      // validation behavior (also preserves 0 correctly).
      const normalizedMaxBudget = preprocessBudgetValue(
        formValues.max_budget_usd,
      );

      // Quick client-side checks:
      // - if a budget reset frequency was chosen, require max_budget_usd
      if (
        formValues.budget_reset_frequency &&
        (normalizedMaxBudget === null || normalizedMaxBudget === undefined)
      ) {
        setError("max_budget_usd", {
          type: "manual",
          message: t("apiKeys.maxBudgetNote"),
        });
        return;
      }

      // - if a max_budget_usd was entered, require budget_reset_frequency
      if (
        normalizedMaxBudget !== null &&
        normalizedMaxBudget !== undefined &&
        String(normalizedMaxBudget).trim() !== "" &&
        (!formValues.budget_reset_frequency ||
          String(formValues.budget_reset_frequency).trim() === "")
      ) {
        setError("budget_reset_frequency", {
          type: "manual",
          message: t("apiKeys.budgetResetFrequencyNote"),
        });
        return;
      }

      const parsedForm = createApiKeySchema.safeParse({
        ...formValues,
        name: formValues.name.trim(),
        projectId: formValues.projectId,
        // Use normalized value; pass undefined only when absent so 0 is preserved
        max_budget_usd:
          normalizedMaxBudget === null || normalizedMaxBudget === undefined
            ? undefined
            : normalizedMaxBudget,
        budget_reset_frequency:
          formValues.budget_reset_frequency &&
          String(formValues.budget_reset_frequency).trim() !== ""
            ? formValues.budget_reset_frequency
            : undefined,
        expiresIn: formValues.expiresIn,
      });

      clearErrors();

      if (!parsedForm.success) {
        const fieldErrors = parsedForm.error.flatten().fieldErrors;
        const nameError = fieldErrors.name?.[0];
        const projectError = fieldErrors.projectId?.[0];
        const maxBudgetError = fieldErrors.max_budget_usd?.[0];
        const budgetResetFrequencyError =
          fieldErrors.budget_reset_frequency?.[0];
        const expiresInError = fieldErrors.expiresIn?.[0];

        if (nameError) {
          setError("name", {
            type: "manual",
            message: t(nameError),
          });
        }

        if (projectError) {
          setError("projectId", {
            type: "manual",
            message: t(projectError),
          });
        }

        if (maxBudgetError) {
          setError("max_budget_usd", {
            type: "manual",
            message: t(maxBudgetError),
          });
          return;
        }

        if (budgetResetFrequencyError) {
          setError("budget_reset_frequency", {
            type: "manual",
            message: budgetResetFrequencyError,
          });
          return;
        }

        if (expiresInError) {
          setError("expiresIn", {
            type: "manual",
            message: expiresInError,
          });
          return;
        }

        return;
      }

      if (parsedForm.success) {
        createKey(parsedForm.data);
      }
    },
    [
      clearErrors,
      createKey,
      getValues,
      mutation.isPending,
      projectScope.principalId,
      projectScopeInitialized,
      projectsQuery.data,
      projectScopeIdentity,
      setError,
      setValue,
      t,
    ],
  );

  return (
    <>
      <Dialog
        open={open && !createdKey}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !isCreatingKey) {
            handleFormClose();
          }
        }}
      >
        <DialogContent
          className="border-border bg-card h-[min(470px,calc(100dvh-2rem))] gap-0 overflow-hidden rounded-[10px] p-0 sm:max-w-[640px] data-[state=open]:slide-in-from-bottom-4 data-[state=open]:duration-300"
          showCloseButton={!isCreatingKey}
          onCloseAutoFocus={(event) => {
            event.preventDefault();

            if (!mounted.current || activeSession.current !== session || generatedKeyHandoffRef.current ||
              document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) {
              return;
            }

            const trigger = returnFocusRef?.current;
            if (trigger?.isConnected && !trigger.disabled) {
              trigger.focus();
            }
          }}
        >
          <form
            onSubmit={handleCreateKey}
            className="flex h-full min-h-0 flex-col"
          >
            <DialogHeader className="border-border h-14 shrink-0 justify-center border-b px-4 pe-12 text-start">
              <DialogTitle className="text-foreground text-xl leading-6 font-semibold tracking-[-0.4px]">
                {t("apiKeys.createNewKey")}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {t("apiKeys.createDialogDescription")}
              </DialogDescription>
            </DialogHeader>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="api-key-name"
                    className="text-secondary-foreground text-xs leading-[18px] font-medium tracking-[0.18px]"
                  >
                    {t("apiKeys.keyNameLabel")}
                  </label>
                  <Input
                    id="api-key-name"
                    placeholder={t("apiKeys.keyNamePlaceholder")}
                    className="border-border bg-input text-foreground placeholder:text-muted-foreground h-9 rounded-lg px-3"
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={errors.name ? "api-key-name-error" : undefined}
                    autoFocus
                    {...register("name", {
                      onChange: (event) => {
                        if (event.target.value.trim()) {
                          clearErrors("name");
                        }
                      },
                    })}
                  />
                  {errors.name && (
                    <p
                      id="api-key-name-error"
                      role="alert"
                      className="text-destructive text-xs"
                    >
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="api-key-project"
                    className="text-secondary-foreground text-xs leading-[18px] font-medium tracking-[0.18px]"
                  >
                    {t("apiKeys.selectProject")}
                  </label>
                  <Controller
                    name="projectId"
                    control={control}
                    render={({ field }) => (
                      <ProjectCombobox
                        options={projects}
                        scopeKey={projectScopeIdentity}
                        label={t("apiKeys.selectProject")}
                        ref={field.ref}
                        onBlur={field.onBlur}
                        id="api-key-project"
                        className="border-border bg-input text-foreground h-9 w-full rounded-lg px-3"
                        aria-invalid={Boolean(errors.projectId)}
                        aria-describedby={errors.projectId ? "api-key-project-error"
                          : isProjectsLoading || projectsQuery.isError || projects.length === 0
                            ? "api-key-project-status" : undefined}
                        placeholder={isProjectsLoading ? t("apiKeys.loadingProjects") : t("apiKeys.selectProject")}
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                          if (value) {
                            clearErrors("projectId");
                          }
                        }}
                        disabled={isProjectsLoading || projects.length === 0}
                      />
                    )}
                  />
                  {(isProjectsLoading || projectsQuery.isError || projects.length === 0) && (
                    <div
                      id="api-key-project-status"
                      aria-live="polite"
                      className="text-muted-foreground flex items-center gap-2 text-xs"
                    >
                      {isProjectsLoading && <Spinner className="size-3.5" />}
                      <span className={projectsQuery.isError ? "text-destructive" : undefined}>
                        {isProjectsLoading
                          ? t("apiKeys.loadingProjects")
                          : projectsQuery.isError
                            ? t("apiKeys.failedToLoadProjects")
                            : t("apiKeys.noProjectsAvailable")}
                      </span>
                    </div>
                  )}
                  {errors.projectId && (
                    <p
                      id="api-key-project-error"
                      role="alert"
                      className="text-destructive text-xs"
                    >
                      {errors.projectId.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="api-key-max-budget"
                    className="text-secondary-foreground text-xs leading-[18px] font-medium tracking-[0.18px]"
                  >
                    {t("apiKeys.maxBudget")}
                  </label>
                  <Input
                    id="api-key-max-budget"
                    placeholder={t("apiKeys.maxBudget")}
                    type="number"
                    min="0"
                    step="0.01"
                    className="border-border bg-input text-foreground placeholder:text-muted-foreground h-9 rounded-lg px-3"
                    aria-invalid={Boolean(errors.max_budget_usd)}
                    aria-describedby={errors.max_budget_usd ? "api-key-max-budget-error" : undefined}
                    {...register("max_budget_usd", {
                      onChange: (event) => {
                        if (event.target.value.trim()) {
                          clearErrors("max_budget_usd");
                        }
                      },
                    })}
                  />
                  {errors.max_budget_usd && (
                    <p
                      id="api-key-max-budget-error"
                      role="alert"
                      className="text-destructive text-xs"
                    >
                      {errors.max_budget_usd.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="api-key-budget-frequency"
                    className="text-secondary-foreground text-xs leading-[18px] font-medium tracking-[0.18px]"
                  >
                    {t("apiKeys.budgetResetFrequency")}
                  </label>
                  <Controller
                    name="budget_reset_frequency"
                    control={control}
                    render={({ field }) => (
                      <Select
                        dir={i18n.dir()}
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value === "NONE" ? "" : value);
                          clearErrors("budget_reset_frequency");
                        }}
                      >
                        <SelectTrigger
                          id="api-key-budget-frequency"
                          className="border-border bg-input text-foreground h-9 w-full rounded-lg px-3"
                          aria-invalid={Boolean(errors.budget_reset_frequency)}
                          aria-describedby={
                            errors.budget_reset_frequency
                              ? "api-key-budget-frequency-error"
                              : undefined
                          }
                        >
                          <SelectValue placeholder={t("apiKeys.budgetResetFrequency")} />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          <SelectItem value="NONE">{t("common.none")}</SelectItem>
                          <SelectItem value="HOURLY">{t("apiKeys.hourly")}</SelectItem>
                          <SelectItem value="DAILY">{t("apiKeys.daily")}</SelectItem>
                          <SelectItem value="WEEKLY">{t("apiKeys.weekly")}</SelectItem>
                          <SelectItem value="MONTHLY">{t("apiKeys.monthly")}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.budget_reset_frequency && (
                    <p
                      id="api-key-budget-frequency-error"
                      role="alert"
                      className="text-destructive text-xs"
                    >
                      {errors.budget_reset_frequency.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="api-key-expiration"
                    className="text-secondary-foreground text-xs leading-[18px] font-medium tracking-[0.18px]"
                  >
                    {t("apiKeys.expiresOptional")}
                  </label>
                  <Controller
                    name="expiresIn"
                    control={control}
                    render={({ field }) => (
                      <Select dir={i18n.dir()} value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger
                          id="api-key-expiration"
                          className="border-border bg-input text-foreground h-9 w-full rounded-lg px-3"
                          aria-invalid={Boolean(errors.expiresIn)}
                          aria-describedby={errors.expiresIn ? "api-key-expiration-error" : undefined}
                        >
                          <SelectValue placeholder={t("apiKeys.selectExpiration")} />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          <SelectItem value="7">{t("apiKeys.7days")}</SelectItem>
                          <SelectItem value="30">{t("apiKeys.30days")}</SelectItem>
                          <SelectItem value="60">{t("apiKeys.60days")}</SelectItem>
                          <SelectItem value="90">{t("apiKeys.90days")}</SelectItem>
                          <SelectItem value="180">{t("apiKeys.6months")}</SelectItem>
                          <SelectItem value="365">{t("apiKeys.1year")}</SelectItem>
                          <SelectItem value="never">{t("common.never")}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.expiresIn && (
                    <p
                      id="api-key-expiration-error"
                      role="alert"
                      className="text-destructive text-xs"
                    >
                      {errors.expiresIn.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter className="border-border min-h-[68px] shrink-0 flex-row flex-wrap items-center justify-between gap-2 border-t px-4 py-4">
              <div className="ms-auto flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleFormClose}
                  className="border-border bg-card text-foreground hover:bg-accent h-9 px-4"
                  disabled={isCreatingKey}
                >
                  {t("common.discard")}
                </Button>
                <Button
                  type="submit"
                  disabled={isCreatingKey}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4"
                  aria-busy={isCreatingKey}
                >
                  {isCreatingKey ? (
                    <>
                      <Spinner className="size-4" />
                      <span>{t("common.creating")}</span>
                    </>
                  ) : (
                    t("apiKeys.createKey")
                  )}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ApiKeySuccessDialog
        open={createdKey !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            handleCreatedKeyClose();
          }
        }}
        title={t("apiKeys.keyCreated")}
        apiKey={createdKey}
        returnFocusRef={returnFocusRef}
        note={t("apiKeys.saveKeyNote")}
      />
    </>
  );
};

export default CreateApiKeyDialog;
